import { api } from "../ApiState";
import { IApiResponse } from "../../Types/ApiResponse.model";
import { setActiveGateway } from "../Slice/Gateway.state";

export const gatewayApi = api.injectEndpoints({
  endpoints: (builder) => ({
    // list
    getGatewayList: builder.query<
      IApiResponse<any>,
      { search?: object; page?: number; deleted?: boolean }
    >({
      query: ({ search, page, deleted }) => ({
        url: "gateway/list",
        method: "GET",
        params: {
          ...(search && { search: JSON.stringify({ ...search }) }),
          ...(page && { page }),
          ...(deleted && { deleted }),
        },
      }),
      providesTags: ["Gateway"],
    }),

    // show
    showGateway: builder.query<any, number>({
      query: (id) => ({
        url: `gateway/${id}`,
        method: "GET",
      }),
      // handle save gateway info
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          const {
            data: { response },
          } = await queryFulfilled;

          if (response?.status === "success") {
            const gateway = response?.data && response.data;
            if (gateway) {
              dispatch(setActiveGateway({ ...gateway }));
            }
          }
        } catch (error) {}
      },
      providesTags: ["Gateway"],
    }),

    // store
    storeGateway: builder.mutation<any, any>({
      query: (data) => ({
        url: "gateway",
        method: "POST",
        body: data,
      }),
      invalidatesTags: (result) => (result ? [{ type: "Gateway" }] : []),
    }),

    // update
    updateGateway: builder.mutation<
      IApiResponse<any>,
      { id: number; value: object }
    >({
      query: ({ id, value }) => ({
        url: `gateway/${id}`,
        method: "POST",
        body: { ...value, _method: "PUT" },
      }),
      invalidatesTags: (result) => (result ? [{ type: "Gateway" }] : []),
    }),

    // delete
    deleteGateway: builder.mutation<IApiResponse<any>, number>({
      query: (id) => ({
        url: `gateway/delete/${id}`,
        method: "POST",
        body: { _method: "DELETE" },
      }),
      invalidatesTags: (result) => (result ? [{ type: "Gateway" }] : []),
    }),

    // notification setting
    gatewayNotificationSetting: builder.mutation<any, number>({
      query: (id) => ({
        url: "notification/show",
        method: "POST",
        body: { gateway_id: id },
      }),

      transformResponse(baseQueryReturnValue: IApiResponse) {
        return baseQueryReturnValue?.response?.data;
      },
    }),

    // set new notification setting
    storeGatewayNotification: builder.mutation<
      any,
      { setting: string; gateway_id: number }
    >({
      query: (data) => ({
        url: "notification/set-setting",
        method: "POST",
        body: data,
      }),
    }),

    // status
    activateGatewayStatus: builder.mutation<IApiResponse<any>, number>({
      query: (id) => ({
        url: `gateway/change-status-to-activated/${id}`,
        method: "POST",
        body: { _method: "PUT" },
      }),
      invalidatesTags: (result) => (result ? [{ type: "Gateway" }] : []),
    }),

    rejectGatewayStatus: builder.mutation<
      IApiResponse<any>,
      { id: number; comment: any }
    >({
      query: ({ id, comment }) => ({
        url: `gateway/change-status-to-rejected/${id}`,
        method: "POST",
        body: { _method: "PUT", comment: comment },
      }),
      invalidatesTags: (result) => (result ? [{ type: "Gateway" }] : []),
    }),

    pendingGatewayStatus: builder.mutation<IApiResponse<any>, number>({
      query: (id) => ({
        url: `gateway/change-status-to-pending/${id}`,
        method: "POST",
        body: { _method: "PUT" },
      }),
      invalidatesTags: (result) => (result ? [{ type: "Gateway" }] : []),
    }),

    // shaparak section
    getShaparakRequestList: builder.query<
      IApiResponse<any>,
      { gateway_id: number; search?: object; page?: number; deleted?: boolean }
    >({
      query: ({ gateway_id, search, page, deleted }) => ({
        url: `gateway/settings/shaparak/list/${gateway_id}`,
        method: "GET",
        params: {
          ...(search && { search: JSON.stringify({ ...search }) }),
          ...(page && { page }),
          ...(deleted && { deleted }),
        },
      }),
      providesTags: ["Shaparak"],
    }),

    getShaparakInfo: builder.query<any, number>({
      query: (gateway_id) => ({
        url: `gateway/settings/shaparak/${gateway_id}`,
        method: "GET",
      }),

      transformResponse(baseQueryReturnValue: IApiResponse) {
        return baseQueryReturnValue?.response?.data;
      },
    }),

    storeShaparakRequest: builder.mutation<
      any,
      { gateway_id: number; data: object }
    >({
      query: ({ gateway_id, data }) => ({
        url: `gateway/settings/shaparak/${gateway_id}`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (result) => (result ? [{ type: "Shaparak" }] : []),
    }),

    updateShaparakRequest: builder.mutation<
      IApiResponse<any>,
      { gateway_id: number; shaparak_id: number; data: object }
    >({
      query: ({ gateway_id, shaparak_id, data }) => ({
        url: `gateway/settings/shaparak/gateway/${gateway_id}`,
        method: "POST",
        body: { ...data, _method: "PUT" },
      }),
      invalidatesTags: (result) => (result ? [{ type: "Shaparak" }] : []),
    }),
  }),
});

export const {
  useGetGatewayListQuery,
  useShowGatewayQuery,
  useStoreGatewayMutation,
  useUpdateGatewayMutation,
  useDeleteGatewayMutation,
  useGatewayNotificationSettingMutation,
  useStoreGatewayNotificationMutation,
  useActivateGatewayStatusMutation,
  useRejectGatewayStatusMutation,
  usePendingGatewayStatusMutation,
  useGetShaparakRequestListQuery,
  useGetShaparakInfoQuery,
  useStoreShaparakRequestMutation,
  useUpdateShaparakRequestMutation,
} = gatewayApi;

export default gatewayApi;
